<template>
  <div :class="$style.board">
    <div v-if="hasBoard" :class="$style.schedule" :style="scheduleStyle">
      <div v-if="hasSchedule" :class="[$style.headerCell, $style.timeHeader]">
        <img src="/icons/clock.svg" alt="" :class="$style.clock" />
      </div>
      <div
        v-for="column in appointmentColumns"
        :key="`head-${column.id}`"
        :class="$style.headerCell"
      >
        <div :class="$style.title">ПОСТ-{{ column.index }}</div>
        <div :class="$style.online">По записи</div>
      </div>
      <div
        v-for="column in liveColumns"
        :key="`head-${column.id}`"
        :class="[$style.headerCell, $style.liveHeader]"
        :style="{ gridColumn: column.gridColumn }"
      >
        <div :class="$style.title">ПОСТ-{{ column.index }}</div>
        <div :class="$style.liveBadge">Живая очередь</div>
      </div>

      <div
        v-for="hour in hours"
        :key="`hour-${hour.hour}`"
        :class="[
          $style.timeHour,
          !hour.hasOrders && $style.timeHourEmpty,
          hour.hasOrders && !hour.marks.length && $style.timeMinuteHourEnd,
        ]"
        :style="gridArea(1, hour.startRow, 1, hour.startRow + 1)"
      >
        {{ hour.label }}
      </div>
      <div
        v-for="mark in timeMarks"
        :key="mark.id"
        :class="[$style.timeMinute, mark.isHourEnd && $style.timeMinuteHourEnd]"
        :style="gridArea(1, mark.startRow, 1, mark.endRow)"
      >
        {{ mark.label }}
      </div>

      <div
        v-for="cell in scheduleCells"
        :key="cell.id"
        :class="[
          $style.cell,
          cell.order ? $style.orderCell : $style.emptyCell,
          cell.isHourEnd && $style.cellHourEnd,
        ]"
        :style="
          gridArea(cell.gridColumn, cell.startRow, cell.gridColumn, cell.endRow)
        "
      >
        <OrderCard
          v-if="cell.order"
          :order="cell.order"
          :isFirst="cell.isFirst"
        />
      </div>

      <div
        v-for="column in liveColumns"
        :key="`body-${column.id}`"
        :class="[$style.cell, $style.liveBody]"
        :style="liveBodyStyle(column)"
      >
        <div :class="$style.liveOrders">
          <OrderCard
            v-for="(order, index) in column.orders"
            :key="order.id"
            :order="order"
            :isFirst="index === 0"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapState } from "vuex";
import OrderCard from "./OrderCard.vue";
import {
  buildColumnCells,
  buildHourRows,
  getCurrentMinutes,
  getHourRange,
  getNowSlot,
  getOccupiedSlots,
  getTodayWorkDay,
  isHourEndRow,
} from "@/utils/timeline";

const HEADER_ROW = 1;

export default {
  name: "Board",
  components: {
    OrderCard,
  },
  props: {
    time: {
      type: String,
      required: true,
    },
  },
  computed: {
    ...mapGetters("orders", ["queue"]),
    ...mapState({
      workDays: (state) => state.department.workDays,
    }),
    orderedQueue() {
      if (!Array.isArray(this.queue)) return [];

      const liveQueue = this.queue.filter(
        (item) => item?.setting_fast?.source === "crm"
      );
      const regularQueues = this.queue.filter(
        (item) => item?.setting_fast?.source !== "crm"
      );

      return [...regularQueues, ...liveQueue];
    },
    appointmentItems() {
      return this.orderedQueue.filter(
        (item) => item?.setting_fast?.source !== "crm"
      );
    },
    liveItems() {
      return this.orderedQueue.filter(
        (item) => item?.setting_fast?.source === "crm"
      );
    },
    appointmentOrders() {
      return this.appointmentItems.flatMap((item) => item?.orders || []);
    },
    timeline() {
      const workDay = getTodayWorkDay(this.workDays);
      const currentMinutes = getCurrentMinutes(this.time);
      const { startHour, endHour } = getHourRange(workDay, this.appointmentOrders);

      const viewStart = getNowSlot(currentMinutes);

      return buildHourRows({
        startHour,
        endHour,
        occupiedSlots: getOccupiedSlots(this.appointmentOrders, viewStart),
        currentMinutes,
        viewStartMinutes: viewStart,
      });
    },
    hours() {
      return this.appointmentItems.length ? this.timeline.hours : [];
    },
    rows() {
      return this.appointmentItems.length ? this.timeline.rows : [];
    },
    timeMarks() {
      return this.hours.flatMap((hour) =>
        hour.marks.map((mark) => ({
          id: `mark-${hour.hour}-${mark.minute}`,
          label: mark.minute,
          startRow: hour.startRow + mark.offset,
          endRow: hour.startRow + mark.offset + 1,
          isHourEnd: mark.offset === hour.marks.length,
        }))
      );
    },
    appointmentColumns() {
      return this.appointmentItems.map((item, index) =>
        this.buildQueueColumn(
          item,
          index + 1,
          index + 2,
          `appointment-${index}`
        )
      );
    },
    liveColumns() {
      const timeOffset = this.hasSchedule ? 1 : 0;
      const appointmentCount = this.appointmentColumns.length;

      return this.liveItems.map((item, index) => ({
        id: item?.setting_fast?.id || `live-${index}`,
        index: appointmentCount + index + 1,
        gridColumn: timeOffset + appointmentCount + index + 1,
        orders: item?.orders || [],
      }));
    },
    scheduleCells() {
      return this.appointmentColumns.flatMap((column) =>
        column.cells.map((cell) => ({
          ...cell,
          id: cell.order?.id || `empty-${column.id}-${cell.startRow}`,
          gridColumn: column.gridColumn,
          isFirst: Boolean(cell.order && cell.order.id === column.firstOrderId),
          isHourEnd: isHourEndRow(this.rows, cell.endRow),
        })),
      );
    },
    hasSchedule() {
      return this.appointmentColumns.length > 0;
    },
    hasBoard() {
      return this.hasSchedule || this.liveColumns.length > 0;
    },
    occupiedRows() {
      const rows = new Set();

      this.appointmentColumns.forEach((column) => {
        column.cells.forEach((cell) => {
          if (!cell.order) return;
          for (let row = cell.startRow; row < cell.endRow; row += 1) {
            rows.add(row);
          }
        });
      });

      return rows;
    },
    scheduleStyle() {
      const occupiedRows = this.occupiedRows;
      const rowSizes = this.hasSchedule
        ? this.rows
            .map((row, index) =>
              row.collapsed || !occupiedRows.has(index) ? "5rem" : "11rem",
            )
            .join(" ")
        : "minmax(0, 1fr)";
      const timeColumn = this.hasSchedule ? "5rem" : "";
      const postCount =
        this.appointmentColumns.length + this.liveColumns.length;
      const postColumns = postCount
        ? ` repeat(${postCount}, minmax(0, 1fr))`
        : "";

      return {
        gridTemplateColumns: `${timeColumn}${postColumns}`.trim(),
        gridTemplateRows: `5rem${rowSizes ? ` ${rowSizes}` : ""}`,
      };
    },
  },
  methods: {
    buildQueueColumn(item, index, gridColumn, fallbackId) {
      const orders = item?.orders || [];
      const cells = buildColumnCells(orders, this.rows);

      return {
        id: item?.setting_fast?.id || fallbackId,
        index,
        gridColumn,
        orders,
        firstOrderId: cells.find((cell) => cell.order)?.order?.id,
        cells,
      };
    },
    gridArea(columnStart, rowStart, columnEnd, rowEnd) {
      return {
        gridColumn: `${columnStart} / ${columnEnd + 1}`,
        gridRow: `${rowStart + HEADER_ROW + 1} / ${rowEnd + HEADER_ROW + 1}`,
      };
    },
    liveBodyStyle(column) {
      const endRow = this.hasSchedule ? this.rows.length + HEADER_ROW + 1 : 3;

      return {
        gridColumn: `${column.gridColumn} / ${column.gridColumn + 1}`,
        gridRow: `2 / ${endRow}`,
      };
    },
  },
};
</script>

<style lang="scss" module>
.board {
  background-color: $bg-primary;
  min-width: 0;
}

.schedule {
  min-width: 0;
  min-height: 100%;
  display: grid;
  border-right: 1px solid $t-primary;
}

.headerCell {
  position: sticky;
  top: 0;
  z-index: 2;
  min-height: 5rem;
  padding: 1rem;
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 1rem;
  background-color: $bg-primary;
  border-left: 1px solid $t-primary;
  border-bottom: 1px solid $t-primary;
  min-width: 0;
}

.timeHeader {
  justify-content: center;
  align-items: center;
  border-left: none;
  padding: 1rem 0;
  left: 0;
  z-index: 3;
}

.clock {
  width: 3rem;
  height: 3rem;
  display: block;
}

.title {
  font-size: 2.25rem;
  font-weight: 600;
  line-height: 1.2;
  min-width: 0;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}

.online,
.liveBadge {
  font-size: 1.25rem;
  font-weight: 600;
  line-height: 1.4;
  padding: 0.625rem 1rem;
  border-radius: 6.25rem;
  white-space: nowrap;
}

.online {
  background-color: $green-light;
  border: 1px solid $green;
}

.liveBadge {
  background-color: $blue-light;
  border: 1px solid $dark-blue;
}

.timeHour,
.timeMinute {
  position: sticky;
  left: 0;
  z-index: 1;
  padding: 0.25rem 1rem 0;
  font-weight: 600;
  text-align: center;
  align-self: start;
  background-color: $bg-primary;
}

.timeHour {
  font-size: 2.25rem;
  line-height: 1.2;
  color: $t-primary;
}

.timeHourEmpty {
  display: flex;
  align-items: center;
  justify-content: center;
  align-self: stretch;
  padding: 0 1rem;
  color: $t-secondary;
  border-bottom: 1px solid $t-primary;
}

.timeMinute {
  font-size: 1.3125rem;
  line-height: 1.4;
  color: $t-secondary;
}

.timeMinuteHourEnd {
  align-self: stretch;
  border-bottom: 1px solid $t-primary;
}

.cell {
  min-width: 0;
  min-height: 0;
  padding: 0.5rem;
  border-left: 1px solid $t-primary;
}

.cellHourEnd {
  border-bottom: 1px solid $t-primary;
}

.orderCell {
  display: flex;
  overflow: hidden;

  > * {
    flex: 1 1 auto;
    min-height: 0;
    overflow: hidden;
  }
}

.emptyCell {
  min-height: 5rem;
}

.liveHeader {
  position: sticky;
  right: 0;
}

.liveBody {
  display: flex;
  align-items: flex-start;
  align-self: stretch;
  min-height: 100%;
  height: 100%;
}

.liveOrders {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  width: 100%;
  min-width: 0;
}
</style>
